import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BookOpen, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function HomeworkSubmission({ user }) {
  const { courseId } = useParams();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/homework/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ course_id: courseId, content })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Homework submitted successfully!');
        navigate(`/courses/${courseId}/view`);
      } else {
        toast.error(data.detail || 'Submission failed');
      }
    } catch (error) {
      toast.error('Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${courseId}/view`)}
            data-testid="back-to-course-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Course
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Submit Homework</h1>
          </div>
          <p className="text-lg text-[#588157]">Share your reflections and learnings from this course</p>
        </div>

        <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="homework" className="text-[#344E41] text-lg mb-2 block">Your Response</Label>
              <Textarea
                id="homework"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={12}
                placeholder="Share your thoughts, reflections, and key learnings from this course..."
                data-testid="homework-textarea"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A] text-[#344E41]"
              />
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/courses/${courseId}/view`)}
                data-testid="cancel-button"
                className="flex-1 border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !content.trim()}
                data-testid="submit-homework-button"
                className="flex-1 bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg"
              >
                <Send className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {loading ? 'Submitting...' : 'Submit Homework'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}